import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { AlertComponent } from "@modules/core/components";
import { Subscription } from "rxjs";
import { getErrorDescriptor } from "../../../utils/error-utils";
import { AudioRecordingService } from "../../modules/core";
import { isFloatEquals } from "./../../../utils/math-utils";

@Component({
  selector: "app-audio-playback-bar",
  templateUrl: "./audio-playback-bar.component.html",
  styleUrls: ["./audio-playback-bar.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AudioPlaybackBarComponent
  implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked, OnChanges
{
  @HostListener("click", ["$event"])
  public onClick(event: MouseEvent): void {
    if (!this.mediaUrl) return;
    event.stopPropagation();
  }

  @ViewChild("audioSlider") audioSlider: ElementRef<HTMLInputElement>;
  @Input() mediaUrl: string;
  @Input() duration: number;
  @Input() color: string = "#fff";
  @Input() buttonColor: string = "#fff";
  @Input() buttonBackgroundColor: string = "rgba(255, 255, 255, 0.3)";

  @HostBinding("style.--playback-bar-color")
  get playbackBarColor() {
    return this.color;
  }

  @HostBinding("style.--playback-bar-button-color")
  get playbackBarButtonColor() {
    return this.buttonColor;
  }

  @HostBinding("style.--playback-bar-button-background-color")
  get playbackBarButtonBackgroundColor() {
    return this.buttonBackgroundColor;
  }

  public isRecording: boolean = false;
  public isPlaying: boolean = false;
  public isTouched: boolean = false;

  public recordingDuration: number = 0;
  public displayDuration: number = 0;
  public showDurationInfo: boolean = false;
  public audio: HTMLAudioElement | null = null;

  private animationFrameId: number | null = null;
  private isViewInitialized = false;
  private subscriptions = new Subscription();
  private wasPlayingBeforeSeek = false;

  private updateSliderOnNextViewCheck: boolean = false;

  public seekValue: number = 0;

  constructor(
    private audioRecordingService: AudioRecordingService,
    private matDialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.audioRecordingService.isRecording$.subscribe((isRecording) => {
        this.isRecording = isRecording;
        this.updateDisplayDuration();
      })
    );
    this.subscriptions.add(
      this.audioRecordingService.isPlaying$.subscribe((isPlaying) => {
        this.isPlaying = isPlaying;
        if (isPlaying) {
          this.animationFrameId = requestAnimationFrame(this.animateSlider);
        } else {
          this.cancelAnimation();
        }
      })
    );
    this.subscriptions.add(
      this.audioRecordingService.recordingDuration$.subscribe((duration) => {
        this.recordingDuration = duration;
        this.updateDisplayDuration();
      })
    );
    this.subscriptions.add(
      this.audioRecordingService.recordingEnded$.subscribe(() => {
        this.isTouched = false;
        this.updateSliderPosition(this.audio.currentTime);
        this.updateDisplayDuration();
      })
    );
    this.updateDisplayDuration();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.duration) {
      this.updateDisplayDuration();
    }
  }

  ngAfterViewInit(): void {
    this.isViewInitialized = true;
    this.updateSliderPosition(this.audio?.currentTime ?? 0);
  }

  ngAfterViewChecked(): void {
    if (!this.updateSliderOnNextViewCheck || !this.audio) return;
    this.updateSliderPosition((this.audio?.currentTime ?? 0) * 1000);

    // Keep trying to update the slider until it's in the correct position
    if (
      isFloatEquals(
        Math.ceil(this.audio?.currentTime * 1000),
        Number(this.audioSlider.nativeElement?.value)
      )
    ) {
      this.updateSliderOnNextViewCheck = false;
    }
  }

  ngOnDestroy(): void {
    this.cancelAnimation();
    this.subscriptions.unsubscribe();
  }

  private cancelAnimation() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.updateDisplayDuration();
  }

  onPlay($event: MouseEvent) {
    $event.stopPropagation();
    this.audioRecordingService.play(this.mediaUrl).subscribe((audio) => {
      if (audio) {
        this.audio = audio;
        this.updateDisplayDuration();
      }
    });
    this.isTouched = true;
    this.updateDisplayDuration();
  }

  onPause($event: MouseEvent) {
    $event.stopPropagation();
    this.audioRecordingService.pause();
    this.updateDisplayDuration();
  }

  onStop($event: MouseEvent) {
    $event.stopPropagation();
    this.audioRecordingService.stopRecording();
    this.updateDisplayDuration();
  }

  public startRecording() {
    this.audioRecordingService.startRecording().subscribe({
      next: (audio) => {
        this.audio = audio;
        this.isTouched = false;
        this.updateSliderOnNextViewCheck = true;
        if (this.audio)
          this.updateSliderPosition(this.audio.currentTime * 1000);
        this.updateDisplayDuration();
      },
      error: (err) => {
        const errorDescriptor = getErrorDescriptor(err);
        AlertComponent.openErrorDialogFromErrorDescriptor(
          this.matDialog,
          errorDescriptor
        );
      },
    });
    this.updateDisplayDuration();
  }

  public deleteRecording() {
    this.audioRecordingService.deleteRecording();
    this.updateDisplayDuration();
  }

  onSeek(event: Event) {
    event.stopPropagation();
    if (!this.audio && this.mediaUrl) return;
    const value = (event.target as HTMLInputElement).value;
    this.audioRecordingService.seek(Number(value));
    this.updateDisplayDuration();
  }

  onSeekEnd(event: MouseEvent) {
    event.stopPropagation();

    if (!this.audio && this.mediaUrl) {
      const value = Number((event.target as HTMLInputElement).value);
      this.updateSliderPosition(value);
      this.audioRecordingService
        .loadUrl(this.mediaUrl, value / 1000)
        .subscribe({
          next: (audio) => {
            this.audio = audio;
            this.isTouched = true;
            this.updateSliderPosition(audio.currentTime * 1000);
          },
        });
      return;
    }

    if (this.wasPlayingBeforeSeek) {
      this.audioRecordingService.play().subscribe();
    }
  }

  onSeekStart(event: MouseEvent) {
    event.stopPropagation();
    if (!this.audio && this.mediaUrl) return;
    this.wasPlayingBeforeSeek = this.isPlaying;
    this.audioRecordingService.pause();
    this.cancelAnimation();
    this.isTouched = true;
    this.updateDisplayDuration();
  }

  private animateSlider = () => {
    this.updateDisplayDuration();

    if (this.audio) {
      this.updateSliderPosition(this.audio.currentTime * 1000);
    }
    if (this.isPlaying) {
      this.animationFrameId = requestAnimationFrame(this.animateSlider);
    }
  };

  private updateSliderPosition(value: number) {
    if (!this.isViewInitialized) return;
    this.audioSlider.nativeElement.value = value.toString();
    this.seekValue = value;
  }

  private updateDisplayDuration(): void {
    this.showDurationInfo =
      !this.isRecording && (this.duration != null || !!this.audio);

    if (!this.showDurationInfo) {
      this.displayDuration = 0;
      return;
    }

    if (this.isTouched) {
      this.displayDuration = this.audio
        ? Math.ceil(this.audio.currentTime * 1000)
        : this.duration;
    } else {
      const audioDuration = this.audio?.duration;
      if (audioDuration && isFinite(audioDuration)) {
        this.displayDuration = Math.ceil(audioDuration * 1000);
      } else {
        this.displayDuration = this.duration;
      }
    }
    this.displayDuration = this.displayDuration ?? 0;
  }
}
